import { useEffect, useRef, useState } from 'react';
import { fetchProfile, fetchSparePartHistory, saveProfile, submitSparePartRequest, type SparePartHistoryItem } from './api.js';
import { VIN_PREFIXES, type VinPrefix, cleanTail, normalizeVinParts, TAIL_MAX_LENGTH } from './utils.js';
import { VinPicker } from './VinPicker.js';
import type { TrackActivity } from './activity.js';
import type { MaxWebApp } from './max.js';
import { ExpiredSessionNotice } from './ExpiredSessionNotice.js';

const MAX_SOURCE_PHOTO = 15 * 1024 * 1024;
const TARGET_PHOTO = 750_000;
const MAX_PHOTOS = 5;

async function loadImage(file: File): Promise<{ source: CanvasImageSource; width: number; height: number; close?: () => void }> {
  if ('createImageBitmap' in window) {
    const bitmap = await createImageBitmap(file);
    return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
  }
  const url = URL.createObjectURL(file);
  const image = new Image();
  try {
    await new Promise<void>((resolve, reject) => { image.onload = () => resolve(); image.onerror = () => reject(new Error('IMAGE_DECODE')); image.src = url; });
    return { source: image, width: image.naturalWidth, height: image.naturalHeight, close: () => URL.revokeObjectURL(url) };
  } catch (error) { URL.revokeObjectURL(url); throw error; }
}

async function canvasBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return await new Promise<Blob>((resolve, reject) => canvas.toBlob(
    (blob) => blob ? resolve(blob) : reject(new Error('IMAGE_ENCODE')), 'image/jpeg', quality));
}

/** Keeps every prepared upload below the server's per-file limit without publishing originals. */
export async function prepareSparePartPhoto(file: File): Promise<File> {
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > MAX_SOURCE_PHOTO) throw new Error('PHOTO_FORMAT');
  if (file.size <= TARGET_PHOTO) return file;
  const image = await loadImage(file);
  try {
    let scale = Math.min(1, 1600 / Math.max(image.width, image.height));
    for (const quality of [0.86, 0.74, 0.62, 0.5]) {
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const context = canvas.getContext('2d');
      if (!context) throw new Error('IMAGE_ENCODE');
      context.fillStyle = '#fff'; context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image.source, 0, 0, canvas.width, canvas.height);
      const blob = await canvasBlob(canvas, quality);
      if (blob.size <= TARGET_PHOTO) {
        const baseName = file.name.replace(/\.[^.]+$/, '') || 'photo';
        return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
      }
      scale *= 0.82;
    }
    throw new Error('PHOTO_TOO_LARGE');
  } finally { image.close?.(); }
}

type ContactStatus = 'checking' | 'requesting' | 'ready' | 'denied' | 'expired' | 'error';
type SparePartField = 'region' | 'partName' | 'email' | 'vin' | 'photos';
type SparePartFieldErrors = Partial<Record<SparePartField, string>>;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;
const FIELD_IDS: Record<SparePartField, string> = {
  region: 'spare-region', partName: 'spare-name', email: 'spare-email', vin: 'spare-vin', photos: 'spare-photos',
};

export function validateSparePartForm(input: { region: string; partName: string; email: string }): SparePartFieldErrors {
  const errors: SparePartFieldErrors = {};
  if (!input.region.trim()) errors.region = 'Укажите регион.';
  if (!input.partName.trim()) errors.partName = 'Укажите название запчасти.';
  const email = input.email.trim();
  if (email && !EMAIL_PATTERN.test(email)) errors.email = 'Введите email в формате name@example.ru.';
  return errors;
}

function focusField(field: SparePartField) {
  const target = document.getElementById(FIELD_IDS[field]);
  target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) {
    target.focus({ preventScroll: true });
  }
}

function serverField(field: string | null | undefined): SparePartField | null {
  if (field === 'part_name') return 'partName';
  return field === 'region' || field === 'email' || field === 'vin' || field === 'photos' ? field : null;
}

function formatHistoryDate(value: string): string {
  const date = new Date(value.replace(' ', 'T') + (/[zZ]|[+-]\d\d:\d\d$/.test(value) ? '' : 'Z'));
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(date).replace(',', '');
}

export function FindSparePartScreen({ webApp, track, onDone, title = 'Найти запчасть' }: {
  webApp?: MaxWebApp; track: TrackActivity; onDone: () => void; title?: string;
}) {
  const initData = webApp?.initData ?? '';
  const [prefix, setPrefix] = useState<VinPrefix>('X0T');
  const [tail, setTail] = useState('');
  const [region, setRegion] = useState('');
  const [partName, setPartName] = useState('');
  const [email, setEmail] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [loadingPhoto, setLoadingPhoto] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<SparePartFieldErrors>({});
  const [contactStatus, setContactStatus] = useState<ContactStatus>('checking');
  const [contactCheckAttempt, setContactCheckAttempt] = useState(0);
  const [history, setHistory] = useState<SparePartHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyCursor, setHistoryCursor] = useState<number | null>(null);
  const [historyError, setHistoryError] = useState('');
  const contactCheckStarted = useRef(false);

  const clearFieldError = (field: SparePartField) => {
    setFieldErrors((current) => current[field] ? { ...current, [field]: undefined } : current);
    setError('');
  };

  const acceptFullVin = (raw: string): boolean => {
    const normalized = normalizeVinParts(prefix, raw);
    if (!normalized.fullVinDetected) return false;
    setPrefix(normalized.prefix);
    setTail(normalized.tail);
    clearFieldError('vin');
    return true;
  };

  const selectSavedVin = (vin: string) => {
    const matched = VIN_PREFIXES.find((item) => vin.startsWith(item));
    if (!matched) return;
    setPrefix(matched);
    setTail(vin.slice(matched.length));
    clearFieldError('vin');
  };

  const requestAndSaveContact = async () => {
    setContactStatus('requesting');
    try {
      const contact = await webApp?.requestContact?.();
      if (!contact?.phone || !initData) { setContactStatus('denied'); return; }
      const saved = await saveProfile(initData, contact.phone);
      setContactStatus(saved.ok && !!saved.phone ? 'ready' : saved.error?.code === 'EXPIRED' ? 'expired' : 'error');
    } catch { setContactStatus('error'); }
  };

  useEffect(() => {
    if (contactCheckStarted.current) return;
    contactCheckStarted.current = true;
    if (!initData) { setContactStatus('expired'); return; }
    fetchProfile(initData)
      .then((profile) => {
        if (!profile.ok) setContactStatus(profile.error?.code === 'EXPIRED' ? 'expired' : 'error');
        else if (profile.phone) setContactStatus('ready');
        else void requestAndSaveContact();
      })
      .catch(() => setContactStatus('error'));
  }, [contactCheckAttempt, initData, webApp]);

  useEffect(() => {
    let cancelled = false;
    if (!initData) { setHistoryLoading(false); return; }
    setHistoryLoading(true);
    fetchSparePartHistory(initData)
      .then((response) => {
        if (cancelled) return;
        if (response.ok) {
          setHistory(response.items ?? []);
          setHistoryHasMore(response.has_more === true);
          setHistoryCursor(response.next_cursor ?? null);
        }
        else if (response.error?.code !== 'EXPIRED') setHistoryError('Не удалось загрузить историю.');
      })
      .catch(() => { if (!cancelled) setHistoryError('Не удалось загрузить историю.'); })
      .finally(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [initData]);

  const loadMoreHistory = async () => {
    if (!initData || !historyHasMore || historyCursor === null || historyLoadingMore) return;
    setHistoryLoadingMore(true);
    setHistoryError('');
    try {
      const response = await fetchSparePartHistory(initData, historyCursor);
      if (!response.ok) throw new Error('HISTORY_LOAD_FAILED');
      setHistory((current) => {
        const known = new Set(current.map((item) => item.id));
        return [...current, ...(response.items ?? []).filter((item) => !known.has(item.id))];
      });
      setHistoryHasMore(response.has_more === true);
      setHistoryCursor(response.next_cursor ?? null);
    } catch {
      setHistoryError('Не удалось загрузить следующие заявки. Попробуйте ещё раз.');
    } finally {
      setHistoryLoadingMore(false);
    }
  };

  const choosePhotos = async (files: File[]) => {
    clearFieldError('photos');
    if (files.length === 0) return;
    if (photos.length + files.length > MAX_PHOTOS) {
      setFieldErrors((current) => ({ ...current, photos: `Можно приложить не более ${MAX_PHOTOS} фотографий.` }));
      focusField('photos');
      return;
    }
    setLoadingPhoto(true);
    try {
      const prepared: File[] = [];
      for (const file of files) prepared.push(await prepareSparePartPhoto(file));
      setPhotos((current) => [...current, ...prepared]);
    }
    catch {
      setFieldErrors((current) => ({ ...current, photos: 'Не удалось подготовить фото. Выберите JPG, PNG или WebP размером до 15 МБ.' }));
      focusField('photos');
    }
    finally { setLoadingPhoto(false); }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault(); setError('');
    if (!initData) { setError('Откройте мини-приложение заново из MAX.'); return; }
    const validationErrors = validateSparePartForm({ region, partName, email });
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setError('Проверьте выделенные поля.');
      const firstField = (['region', 'partName', 'email'] as SparePartField[]).find((field) => validationErrors[field]);
      if (firstField) focusField(firstField);
      return;
    }
    setFieldErrors({});
    setSubmitting(true);
    try {
      const normalizedVin = tail ? normalizeVinParts(prefix, tail) : null;
      if (normalizedVin?.fullVinDetected) {
        setPrefix(normalizedVin.prefix);
        setTail(normalizedVin.tail);
      }
      const response = await submitSparePartRequest({ initData, region, partName, email,
        vin: normalizedVin?.vin ?? '', photos });
      if (!response.ok) {
        const field = serverField(response.error?.field);
        const message = response.error?.message || 'Не удалось отправить заявку.';
        if (field) {
          setFieldErrors((current) => ({ ...current, [field]: message }));
          setError('Проверьте выделенное поле.');
          focusField(field);
          return;
        }
        throw new Error(message);
      }
      track('find_spare_part', 'request_submit');
      onDone();
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Не удалось отправить заявку.'); }
    finally { setSubmitting(false); }
  };

  return <section className="spare-part" aria-labelledby="spare-part-title">
    <h1 className="station-search__title" id="spare-part-title">{title}</h1>
    <p className="station-search__subtitle">Опишите нужную деталь — заявка поступит специалистам ТОНАР.</p>
    {history.length > 0 ? <a className="spare-part-history__shortcut" href="#spare-part-history">
      <span>История обращений</span><strong>{history.length}{historyHasMore ? '+' : ''}</strong>
    </a> : null}
    <form className="spare-part__form" onSubmit={submit} noValidate>
      <label className="form__label" htmlFor="spare-region">Регион <span aria-hidden="true">*</span></label>
      <input className={`spare-part__input${fieldErrors.region ? ' spare-part__input--error' : ''}`} id="spare-region" value={region}
        onChange={(e) => { setRegion(e.target.value); clearFieldError('region'); }} required maxLength={120}
        autoComplete="address-level1" placeholder="Например, Москва" aria-invalid={!!fieldErrors.region}
        aria-describedby={fieldErrors.region ? 'spare-region-error' : undefined} />
      {fieldErrors.region ? <p className="spare-part__field-error" id="spare-region-error">{fieldErrors.region}</p> : null}
      <label className="form__label" htmlFor="spare-name">Название запчасти <span aria-hidden="true">*</span></label>
      <textarea className={`spare-part__input spare-part__textarea${fieldErrors.partName ? ' spare-part__input--error' : ''}`}
        id="spare-name" value={partName} onChange={(e) => { setPartName(e.target.value); clearFieldError('partName'); }}
        required maxLength={500} rows={3} placeholder="Например, тормозные колодки" aria-invalid={!!fieldErrors.partName}
        aria-describedby={fieldErrors.partName ? 'spare-name-error' : undefined} />
      {fieldErrors.partName ? <p className="spare-part__field-error" id="spare-name-error">{fieldErrors.partName}</p> : null}
      <label className="form__label" htmlFor="spare-email">Email <small>необязательно</small></label>
      <input className={`spare-part__input${fieldErrors.email ? ' spare-part__input--error' : ''}`} id="spare-email" type="email"
        value={email} onChange={(e) => { setEmail(e.target.value); clearFieldError('email'); }} maxLength={254}
        autoComplete="email" inputMode="email" placeholder="name@example.ru" aria-invalid={!!fieldErrors.email}
        aria-describedby={fieldErrors.email ? 'spare-email-error' : undefined} />
      {fieldErrors.email ? <p className="spare-part__field-error" id="spare-email-error">{fieldErrors.email}</p> : null}
      <fieldset className={`spare-part__vin${fieldErrors.vin ? ' spare-part__vin--error' : ''}`}>
        <legend>VIN <span>необязательно</span></legend>
        <div className="form__vin-row">
          <select className="form__prefix" value={prefix} onChange={(e) => { setPrefix(e.target.value as VinPrefix); clearFieldError('vin'); }} aria-label="Префикс VIN запчасти">
            {VIN_PREFIXES.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <input className="form__input form__tail" id="spare-vin" aria-label="VIN запчасти" value={tail}
            onChange={(e) => { setTail(cleanTail(e.target.value)); clearFieldError('vin'); }} maxLength={TAIL_MAX_LENGTH}
            onPaste={(e) => { if (acceptFullVin(e.clipboardData.getData('text'))) e.preventDefault(); }}
            onBlur={(e) => { acceptFullVin(e.currentTarget.value); }}
            autoCapitalize="characters" autoComplete="off" spellCheck={false} aria-invalid={!!fieldErrors.vin}
            aria-describedby={fieldErrors.vin ? 'spare-vin-error' : undefined} />
        </div>
        <VinPicker initData={initData} currentVin={tail ? prefix + tail : ''} refreshKey={history.length}
          onSelect={selectSavedVin} onManualEntry={() => {
            setTail(''); clearFieldError('vin'); setTimeout(() => focusField('vin'), 0);
          }} />
        {fieldErrors.vin ? <p className="spare-part__field-error" id="spare-vin-error">{fieldErrors.vin}</p>
          : <small>Оставьте поле пустым, если VIN неизвестен.</small>}
      </fieldset>
      <label className="spare-part__photo" id="spare-photos">
        <span className="form__label">Фотографии <small>необязательно, до {MAX_PHOTOS}</small></span>
        <input type="file" multiple disabled={loadingPhoto || photos.length >= MAX_PHOTOS} accept="image/jpeg,image/png,image/webp"
          onChange={(e) => { const files = Array.from(e.target.files ?? []); e.target.value = ''; void choosePhotos(files); }} />
        <span className="spare-part__photo-button">{loadingPhoto ? 'Подготавливаем фото…'
          : photos.length >= MAX_PHOTOS ? `Добавлено ${MAX_PHOTOS} из ${MAX_PHOTOS}`
            : photos.length > 0 ? `Добавить фото (${photos.length}/${MAX_PHOTOS})` : 'Приложить фотографии'}</span>
      </label>
      {fieldErrors.photos ? <p className="spare-part__field-error">{fieldErrors.photos}</p> : null}
      {photos.length > 0 ? <div className="spare-part__files">{photos.map((photo, index) =>
        <div className="spare-part__file" key={`${photo.name}-${photo.lastModified}-${index}`}>
          <span>{photo.name}</span><button type="button" onClick={() => setPhotos((current) => current.filter((_, itemIndex) => itemIndex !== index))}>Удалить</button>
        </div>)}</div> : null}
      {contactStatus === 'checking' || contactStatus === 'requesting'
        ? <p className="spare-part__contact">{contactStatus === 'checking' ? 'Проверяем контакт…' : 'Получаем номер телефона из MAX…'}</p>
        : null}
      {contactStatus === 'denied' ? <div className="spare-part__contact-error" role="alert">
        <span>Для отправки заявки поделитесь номером телефона в MAX.</span>
        <button type="button" onClick={() => void requestAndSaveContact()}>Поделиться номером</button>
      </div> : null}
      {contactStatus === 'expired' ? <ExpiredSessionNotice webApp={webApp} className="spare-part__contact-error" /> : null}
      {contactStatus === 'error' ? <div className="spare-part__contact-error" role="alert">
        <span>Не удалось проверить сохранённый номер.</span>
        <button type="button" onClick={() => {
          contactCheckStarted.current = false;
          setContactStatus('checking');
          setContactCheckAttempt((attempt) => attempt + 1);
        }}>Повторить</button>
      </div> : null}
      {error ? <p className="station-search__message station-search__message--error" role="alert">{error}</p> : null}
      <button className="form__submit" type="submit" disabled={submitting || loadingPhoto || contactStatus !== 'ready'}>
        {submitting ? 'Отправляем…' : 'Отправить заявку'}
      </button>
      <p className="spare-part__privacy">Нажимая кнопку, Вы передаёте данные специалистам ТОНАР для обработки запроса.</p>
    </form>
    <section className="spare-part-history" id="spare-part-history" aria-labelledby="spare-part-history-title">
      <h2 id="spare-part-history-title">История обращений</h2>
      {historyLoading ? <p className="spare-part-history__message">Загружаем историю…</p> : null}
      {historyError ? <p className="spare-part-history__message spare-part-history__message--error">{historyError}</p> : null}
      {!historyLoading && !historyError && history.length === 0
        ? <p className="spare-part-history__message">Здесь появятся ваши заявки на запчасти.</p> : null}
      {history.length > 0 ? <ul className="spare-part-history__list">{history.map((item) =>
        <li className="spare-part-history__card" key={item.id}>
          <div className="spare-part-history__topline">
            <strong>{item.part_name}</strong><time>{formatHistoryDate(item.created_at)}</time>
          </div>
          <div className="spare-part-history__details">
            <span><b>Регион:</b> {item.region}</span>
            {item.vin ? <span><b>VIN:</b> {item.vin}</span> : null}
            {item.article ? <span><b>Артикул:</b> {item.article}</span> : null}
            {item.name ? <span><b>Наименование:</b> {item.name}</span> : null}
          </div>
          <span className={`spare-part-history__status${item.notified ? '' : ' spare-part-history__status--error'}`}>
            {item.notified ? 'Передано специалистам' : 'Не удалось передать'}
          </span>
        </li>)}</ul> : null}
      {historyHasMore ? <button className="spare-part-history__more" type="button" disabled={historyLoadingMore}
        onClick={() => void loadMoreHistory()}>{historyLoadingMore ? 'Загружаем…' : 'Показать ещё'}</button> : null}
    </section>
  </section>;
}
